import React, { useState, useEffect, useCallback } from "react";
import { Bell, Send } from "lucide-react";
import { useParams } from "react-router-dom";
import { adminAPI, notificationsAPI } from "../../services/api";
import "./Admin.css";

const NotificationManager = () => {
  const { eventId } = useParams();
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState(eventId || "");
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [recentNotifications, setRecentNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadEvents();
    loadRecentNotifications();
  }, []);

  useEffect(() => {
    if (eventId) {
      setSelectedEventId(eventId);
    }
  }, [eventId]);

  useEffect(() => {
    loadRecentNotifications();
  }, [selectedEventId]);

  const loadEvents = async () => {
    try {
      const response = await adminAPI.getEvents();
      setEvents(response.data || []);
    } catch (error) {
      console.error("Failed to load events:", error);
    }
  };

  const loadRecentNotifications = async () => {
    setLoading(true);
    try {
      const eventFilter = selectedEventId ? parseInt(selectedEventId) : null;
      const response =
        await notificationsAPI.getAdminNotifications(eventFilter);
      setRecentNotifications(response.data || []);
    } catch (error) {
      console.error("Failed to load notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendNotification = async (e) => {
    e.preventDefault();

    if (!selectedEventId) {
      alert("Please select an event");
      return;
    }

    if (!title.trim() || !message.trim()) {
      alert("Please fill in both title and message");
      return;
    }

    setSending(true);
    try {
      const response = await notificationsAPI.sendNotification({
        title: title.trim(),
        message: message.trim(),
        event_id: parseInt(selectedEventId),
        notification_type: "info",
      });

      alert(`Notification sent to ${response.data.count} users!`);

      // Clear form
      setTitle("");
      setMessage("");

      // Reload recent notifications
      loadRecentNotifications();
    } catch (error) {
      console.error("Failed to send notification:", error);
      alert("Failed to send notification. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="admin-page">
      <div className="admin-section-header" style={{ marginBottom: "24px" }}>
        <h1 style={{ fontSize: "1.5rem", fontWeight: "700" }}>
          Notifications Manager
        </h1>
        <p style={{ color: "var(--admin-text-sub)" }}>
          Broadcast messages and alerts to all attendees
        </p>
      </div>

      <div className="admin-card" style={{ maxWidth: "600px" }}>
        <div className="card-header">
          <h2>Send New Announcement</h2>
        </div>
        <form onSubmit={handleSendNotification} style={{ padding: "24px" }}>
          {/* Event Selector */}
          <div style={{ marginBottom: "20px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: "600",
                fontSize: "0.875rem",
              }}
            >
              Select Event *
            </label>
            <select
              value={selectedEventId}
              onChange={(e) => setSelectedEventId(e.target.value)}
              required
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: "8px",
                border: "1px solid var(--admin-border)",
                outline: "none",
              }}
            >
              <option value="">-- Select Event --</option>
              {events.map((event) => (
                <option key={event.id} value={event.id}>
                  {event.title}
                </option>
              ))}
            </select>
          </div>

          {/* Message Title */}
          <div style={{ marginBottom: "20px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: "600",
                fontSize: "0.875rem",
              }}
            >
              Message Title *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Session starting in 10 minutes"
              required
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: "8px",
                border: "1px solid var(--admin-border)",
                outline: "none",
              }}
            />
          </div>

          {/* Content */}
          <div style={{ marginBottom: "20px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: "600",
                fontSize: "0.875rem",
              }}
            >
              Content *
            </label>
            <textarea
              rows="4"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Enter the notification content here..."
              required
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: "8px",
                border: "1px solid var(--admin-border)",
                outline: "none",
                resize: "vertical",
              }}
            />
          </div>

          <button
            type="submit"
            className="admin-btn btn-primary"
            disabled={sending}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              justifyContent: "center",
              width: "100%",
            }}
          >
            <Send size={18} />
            {sending ? "Sending..." : "Send Notification"}
          </button>
        </form>
      </div>

      {/* Recent Notifications */}
      <div className="admin-card" style={{ marginTop: "24px" }}>
        <div className="card-header">
          <h2>Recent Notifications</h2>
        </div>
        <div style={{ padding: "24px" }}>
          {loading ? (
            <div
              style={{ textAlign: "center", color: "var(--admin-text-sub)" }}
            >
              Loading...
            </div>
          ) : recentNotifications.length === 0 ? (
            <div
              style={{ textAlign: "center", color: "var(--admin-text-sub)" }}
            >
              <Bell size={48} style={{ opacity: 0.2, marginBottom: "16px" }} />
              <p>No previous notifications found.</p>
            </div>
          ) : (
            <div
              style={{ display: "flex", flexDirection: "column", gap: "12px" }}
            >
              {recentNotifications.map((notif) => (
                <div
                  key={notif.id}
                  style={{
                    padding: "16px",
                    background: "#f8fafc",
                    borderRadius: "8px",
                    borderLeft: "4px solid #3b82f6",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: "8px",
                    }}
                  >
                    <strong style={{ fontSize: "14px" }}>{notif.title}</strong>
                    <span style={{ fontSize: "12px", color: "#64748b" }}>
                      {formatDate(notif.created_at)}
                    </span>
                  </div>
                  <p style={{ margin: 0, fontSize: "13px", color: "#475569" }}>
                    {notif.message}
                  </p>
                  {notif.event_id && (
                    <div
                      style={{
                        marginTop: "8px",
                        fontSize: "11px",
                        color: "#94a3b8",
                      }}
                    >
                      Event ID: {notif.event_id}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationManager;
